import { type match, useHistory, Link } from 'react-router-dom'
import { useQueryParams } from '~/utils/useQueryParams'

import type { ColumnProps } from 'antd/lib/table'
import { Button, Card, Flex, Heading } from '@weareberyl/design-system'

import { prependScheme, ADDON_CREATE } from '~/constants/routes'
import HeadTitle from '~/components/HeadTitle'
import Table from '~/components/Table'

import {
  useSchemeAddons,
  defaultData,
  type UseSchemeAddons,
  type SchemeAddonListItem,
} from './data'
import { useCurrentUser } from '~/hooks'
import { AddonTypeTag } from '../components'
import { hasPermission } from '~/components/Session'

type SchemeAddonComponentProps = {
  setQueryParams: (params: { current?: number; pageSize?: number }) => void
  schemeId: string
} & UseSchemeAddons

const SchemeAddonComponent = ({
  setQueryParams,
  data,
  loading,
  schemeId,
}: SchemeAddonComponentProps) => {
  const history = useHistory()
  const [user] = useCurrentUser()
  const columns: ColumnProps<SchemeAddonListItem>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
    },
    {
      title: '',
      width: 50,
      render: ({ is_protected, is_discoverable }: SchemeAddonListItem) => {
        const icons: string[] = []

        if (is_protected) {
          icons.push('🔒')
        }
        if (!is_discoverable) {
          icons.push('🎟️')
        }
        return <span>{icons.join(' ')}</span>
      },
    },
    {
      title: 'Name',
      width: 200,
      render: ({ name, is_active, id }: SchemeAddonListItem) => {
        let title = <span>{name}</span>
        if (!is_active) {
          title = (
            <span>
              <s>{name}</s> (inactive)
            </span>
          )
        }

        return (
          <Link to={`addons/${id}`}>
            <strong>{title}</strong>
          </Link>
        )
      },
    },
    {
      title: 'Addon type',
      dataIndex: 'addon_type',
      render: addon_type => {
        return <AddonTypeTag addonType={addon_type} />
      },
    },
    {
      title: 'Price',
      dataIndex: ['price', 'formatted_amount'],
    },
  ]

  if (user && hasPermission(user.roles, 'product.edit', parseInt(schemeId))) {
    columns.push({
      title: 'Edit',
      dataIndex: 'id',
      render: id => {
        return <Link to={`addons/${id}/edit`}>Edit</Link>
      },
    })
  }

  const tableData = data ?? defaultData

  return (
    <Table<SchemeAddonListItem>
      id={id}
      onChange={({ current, pageSize }) =>
        setQueryParams({ current, pageSize })
      }
      columns={columns}
      rowClassName={({ is_active, is_discoverable }: SchemeAddonListItem) => {
        if (!is_active) {
          return 'inactive-row'
        }
        if (!is_discoverable) {
          return 'subdued-row'
        }
        return ''
      }}
      rowKey={'id'}
      data={tableData}
      loading={loading}
      onRow={({ id }) => ({
        onDoubleClick() {
          history.push(`addons/${id}`)
        },
      })}
    />
  )
}

const id = 'addons-table'

type Props = {
  match: match<{ scheme_id: string }>
}

const AddonList = ({ match }: Props) => {
  const schemeId = match.params.scheme_id
  const history = useHistory()
  const [{ current, pageSize }, setQueryParams] = useQueryParams(id, {})
  const { data, loading } = useSchemeAddons({ current, pageSize, schemeId })

  return (
    <>
      <HeadTitle pageTitle="Addons" />
      <Card p={5}>
        <Flex justifyContent="space-between" alignItems="flex-end" mb={5}>
          <Heading variant="callout">Addons</Heading>
          <Button
            title="Create new addon"
            onClick={() => {
              history.push(prependScheme(ADDON_CREATE, schemeId))
            }}
          />
        </Flex>
        <SchemeAddonComponent
          setQueryParams={setQueryParams}
          data={data}
          loading={loading}
          schemeId={schemeId}
        />
      </Card>
    </>
  )
}

export default AddonList
